/*
 * Bulk actions on sites (docs/api.md, "Bulk actions"): `POST /orgs/:orgId/sites/bulk { siteIds, action, params, dryRun }`
 * plans (dryRun: what each site would get, for the confirm dialog) or starts one run over up to 200 sites;
 * `GET /orgs/:orgId/bulk/:bulkId` and `GET /orgs/:orgId/bulk?recent=1` read runs; a `bulk` event on the person's
 * stream carries a run's new state. A run is one grouped task in the tray, with a row per site.
 *
 * The Updates page's runs ("updates.selected", api/orgUpdates.ts) come the same way, with a row per environment or
 * server update instead of per site.
 */
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { useOrg } from "../app/org";
import { useNow } from "../lib/hooks";
import { api } from "./client";
import { keys } from "./keys";
import type { AutoUpdates, Operation } from "./types";
import type { Work } from "./work";

export type BulkAction = "tags.add" | "tags.remove" | "cache.purge" | "backup.create" | "health.check" | "updates.install" | "autoUpdates.set" | "security.profile";
/** What a run does: a Sites page action, or the Updates page's chosen updates. */
export type RunAction = BulkAction | "updates.selected";

export type BulkParams = {
  /** tags.add, tags.remove. */
  tags?: string[];
  /** tags.add: the colors of tags new to the org (as on PATCH /sites/:id). */
  colors?: Record<string, string>;
  /** updates.install: security fixes only, or every update on offer. */
  scope?: "security" | "all";
  /** updates.install: a restore point of each site's Live first (the API's default is true). */
  restorePoint?: boolean;
  /** autoUpdates.set. */
  mode?: AutoUpdates;
  /** security.profile: the profile every environment the caller can manage on each site follows. */
  profileId?: string;
  /** security.profile: keep what was changed on an environment itself (the API's default is false). */
  keepOverrides?: boolean;
};

/**
 * One update in an item, with its versions ("LiteSpeed Cache 7.9.1 → 7.9.2"; a PHP release "PHP 8.3"); a security fix
 * marked as on the Updates page. Once its run reported, `from` and `to` are what the server says and `status` whether
 * it installed.
 */
export type UpdateLine = {
  type: "core" | "plugin" | "theme" | "php" | "node";
  slug: string;
  name: string;
  from: string | null;
  to: string | null;
  security: boolean;
  status: "updated" | "rolled_back" | "skipped" | "failed" | null;
};

/** A site in a plan (will_run, skipped) or a run (queued, running, succeeded, failed, skipped). */
export type BulkItemStatus = "will_run" | "skipped" | "queued" | "running" | "succeeded" | "failed";

export type BulkItem = {
  /** The site's id; on an Updates page run, "env:<id>", "node:<id>" or "server:<id>:php" / ":os" / ":all". */
  key: string;
  kind: "site" | "environment" | "server";
  /** Empty for a server's item. */
  siteId: string;
  /** A server's item (Updates page runs): its server, for the log link. */
  serverId: string | null;
  /** How the row reads: "KN Beta · Live", "test-1 · PHP 8.3.36", or the site's name. */
  label: string | null;
  /** Null from the API for a site the person can't see. */
  siteName: string | null;
  status: BulkItemStatus;
  /** Why it's skipped, in the API's words ("Live isn't ready yet."). */
  reason: string | null;
  /** updates.install and the Updates page's runs: how many updates, what ("WordPress and 2 plugins"), and each with its versions (none from an older API). */
  updates: { items: number; what: string; list: UpdateLine[] } | null;
  /** tags.*: the site's tags after the change. */
  tagsAfter: string[] | null;
  /** Its operation (updates: the restore point's, then the update's). */
  operationId: string | null;
  /** The site's org when it isn't this one (a client's site), for its log link. */
  orgSlug: string | null;
  error: string | null;
};

export type BulkPlan = { action: BulkAction; items: BulkItem[] };

export type BulkRun = {
  id: string;
  /** The org it was asked from. */
  orgId: string | null;
  action: RunAction;
  params: BulkParams;
  /** The API's words while it runs ("Backing up 12 sites", "Updating PHP 8.3 on mail-1"). */
  title: string | null;
  /** The Updates page's runs, once every item settled: what installed ("Installed PHP 8.3.36 on mail-1"). */
  finishedTitle: string | null;
  startedBy: { id: string; name: string } | null;
  createdAt: string;
  finishedAt: string | null;
  items: BulkItem[];
};

/* ── Reading the API's answers ──────────────────────────────────── */

type Raw = Record<string, unknown>;
const str = (v: unknown) => (typeof v === "string" && v ? v : null);
const STATUSES = new Set(["updated", "rolled_back", "skipped", "failed"]);

function linesFrom(v: unknown): UpdateLine[] {
  if (!Array.isArray(v)) return [];
  return (v as Raw[]).flatMap((u) => {
    const type = u.type === "core" || u.type === "plugin" || u.type === "theme" || u.type === "php" || u.type === "node" ? u.type : null;
    if (!type) return [];
    return [{ type, slug: str(u.slug) ?? "", name: str(u.name) ?? str(u.slug) ?? "", from: str(u.from), to: str(u.to), security: u.security === true, status: STATUSES.has(String(u.status)) ? (u.status as UpdateLine["status"]) : null }];
  });
}

function itemFrom(r: Raw, plan: boolean): BulkItem {
  const s = str(r.status) ?? "queued";
  const d = (r.detail ?? null) as Raw | null;
  const updates = d && typeof d.items === "number" ? { items: d.items, what: str(d.what) ?? "", list: linesFrom(d.updates) } : null;
  const tagsAfter = d && Array.isArray(d.tags) ? (d.tags as unknown[]).filter((t): t is string => typeof t === "string") : null;
  // A plan says what would happen: anything not skipped would run. A run's at-once changes come back "done".
  const status: BulkItemStatus = plan ? (s === "skipped" ? "skipped" : "will_run") : s === "done" ? "succeeded" : (s as BulkItemStatus);
  const kind = r.kind === "environment" || r.kind === "server" ? r.kind : "site";
  return {
    key: str(r.key) ?? String(r.siteId ?? ""),
    kind,
    siteId: String(r.siteId ?? ""),
    serverId: kind === "server" ? str(r.serverId) : null,
    label: str(r.label),
    siteName: str(r.siteName),
    status,
    reason: str(r.reason),
    updates,
    tagsAfter,
    operationId: str(r.operationId),
    orgSlug: str(r.orgSlug),
    error: str(r.error),
  };
}

const planFrom = (d: Raw, action: BulkAction): BulkPlan => ({ action, items: ((d.results ?? []) as Raw[]).map((r) => itemFrom(r, true)) });

/** A `BulkProgress`. */
export function runFrom(b: Raw): BulkRun | null {
  const id = str(b.id);
  if (!id) return null;
  return {
    id,
    orgId: str(b.orgId),
    action: (str(b.action) ?? "backup.create") as RunAction,
    params: (b.params ?? {}) as BulkParams,
    title: str(b.title),
    finishedTitle: str(b.finishedTitle),
    startedBy: (b.startedBy as BulkRun["startedBy"]) ?? null,
    createdAt: str(b.createdAt) ?? new Date().toISOString(),
    finishedAt: str(b.finishedAt),
    items: ((b.items ?? []) as Raw[]).map((r) => itemFrom(r, false)),
  };
}

/* ── Words and counts ───────────────────────────────────────────── */

export const isActiveItem = (i: BulkItem) => i.status === "queued" || i.status === "running";

/** How far a run has got: what's in it (skipped ones aside), done, failed, still going. */
export function runCounts(b: BulkRun) {
  const items = b.items.filter((i) => i.status !== "skipped");
  const failed = items.filter((i) => i.status === "failed").length;
  const succeeded = items.filter((i) => i.status === "succeeded").length;
  const running = items.filter((i) => i.status === "running").length;
  return { total: items.length, done: failed + succeeded, failed, succeeded, running, skipped: b.items.length - items.length };
}

export const runFinished = (b: BulkRun) => !!b.finishedAt || b.items.every((i) => !isActiveItem(i));

const n = (count: number) => `${count} ${count === 1 ? "site" : "sites"}`;

/** What a run's title counts: sites, or for the Updates page's runs the updates themselves. */
export const titleCount = (b: Pick<BulkRun, "action">, items: BulkItem[]) => (b.action === "updates.selected" ? items.reduce((sum, i) => sum + (i.updates?.items ?? 1), 0) : items.length);

/** "Show the 12 sites", "Show the 3 environments and 1 server". */
export function itemsWords(b: BulkRun): string {
  if (b.action !== "updates.selected") return `${b.items.length} ${b.items.length === 1 ? "site" : "sites"}`;
  const envs = b.items.filter((i) => i.kind === "environment").length;
  const servers = new Set(b.items.filter((i) => i.kind === "server").map((i) => i.serverId)).size;
  return [envs ? `${envs} ${envs === 1 ? "environment" : "environments"}` : null, servers ? `${servers} ${servers === 1 ? "server" : "servers"}` : null].filter(Boolean).join(" and ");
}

/** "Backing up 12 sites" while it runs; "Backed up 12 sites" after. */
export function runTitle(action: RunAction, count: number, done: boolean): string {
  switch (action) {
    case "updates.selected": {
      const u = `${count} ${count === 1 ? "update" : "updates"}`;
      return done ? `Installed ${u}` : `Installing ${u}`;
    }
    case "backup.create":
      return done ? `Backed up ${n(count)}` : `Backing up ${n(count)}`;
    case "cache.purge":
      return done ? `Cleared the cache on ${n(count)}` : `Clearing the cache on ${n(count)}`;
    case "health.check":
      return done ? `Checked ${n(count)}` : `Checking ${n(count)}`;
    case "updates.install":
      return done ? `Updated ${n(count)}` : `Updating ${n(count)}`;
    case "autoUpdates.set":
      return done ? `Set automatic updates on ${n(count)}` : `Setting automatic updates on ${n(count)}`;
    case "tags.add":
      return done ? `Tagged ${n(count)}` : `Tagging ${n(count)}`;
    case "tags.remove":
      return done ? `Removed tags from ${n(count)}` : `Removing tags from ${n(count)}`;
    case "security.profile":
      return done ? `Set a security profile on ${n(count)}` : `Setting a security profile on ${n(count)}`;
  }
}

/** The dialog's title: "Back up 12 sites". */
export function planTitle(action: BulkAction, count: number, params: BulkParams): string {
  switch (action) {
    case "backup.create":
      return `Back up ${n(count)}`;
    case "cache.purge":
      return `Clear the cache on ${n(count)}`;
    case "health.check":
      return `Check ${n(count)} now`;
    case "updates.install":
      return params.scope === "security" ? `Install security updates on ${n(count)}` : `Install all updates on ${n(count)}`;
    case "autoUpdates.set":
      return `Automatic updates on ${n(count)}`;
    case "tags.add":
      return `Add tags to ${n(count)}`;
    case "tags.remove":
      return `Remove tags from ${n(count)}`;
    case "security.profile":
      return `Apply a security profile to ${n(count)}`;
  }
}

/** The run button: "Back up 11 sites". */
export function runLabel(action: BulkAction, count: number, params: BulkParams): string {
  switch (action) {
    case "backup.create":
      return `Back up ${n(count)}`;
    case "cache.purge":
      return `Clear ${count === 1 ? "1 cache" : `${count} caches`}`;
    case "health.check":
      return `Check ${n(count)}`;
    case "updates.install":
      return `Update ${n(count)}`;
    case "autoUpdates.set":
      return params.mode === "off" ? `Turn them off on ${n(count)}` : `Apply to ${n(count)}`;
    case "tags.add":
      return `Tag ${n(count)}`;
    case "tags.remove":
      return `Remove from ${n(count)}`;
    case "security.profile":
      return `Apply to ${n(count)}`;
  }
}

/* ── The org's runs ─────────────────────────────────────────────── */

/** How long a finished run stays in the tray, as other finished work does. */
export const BULK_RECENT_MS = 10 * 60_000;

/** Actions the API applies during the request. */
const AT_ONCE = new Set<RunAction>(["tags.add", "tags.remove", "autoUpdates.set"]);

export const bulkKey = (orgId: string, id: string) => ["org", orgId, "bulk", id] as const;
export const recentKey = (orgId: string) => ["org", orgId, "bulk-recent"] as const;

// Runs started in this tab, read on their own until the recent list has them (it's read every few seconds anyway),
// and said when they end even if they finished before the tray first saw them running (a health check takes a second).
const startedHere = new Map<string, string[]>();
const announce = new Set<string>();
/** Whether this tab started the run and hasn't said how it ended yet; saying it once. */
export function takeAnnouncement(id: string): boolean {
  return announce.delete(id);
}
const listeners = new Set<() => void>();
const EMPTY: string[] = [];
/** Calls `fn` when this tab starts a run, in any org: the person's work in every org is read again (api/work.ts). */
export function onRunStarted(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
/** A run started from this tab: the tray follows it at once, and says how it ended. */
export function noteStarted(orgId: string, id: string) {
  announce.add(id);
  const list = startedHere.get(orgId) ?? [];
  if (list.includes(id)) return;
  startedHere.set(orgId, [id, ...list].slice(0, 20));
  listeners.forEach((l) => l());
}
function useStartedHere(orgId: string): string[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => startedHere.get(orgId) ?? EMPTY,
  );
}

/** Whether a run shows in the tray: one that runs, or finished in the last ten minutes. */
export const inTray = (b: BulkRun, now: number) =>
  // At-once changes (tags, automatic updates) aren't work to follow: their toast said how they went.
  !AT_ONCE.has(b.action) && (!runFinished(b) || now - Date.parse(b.finishedAt ?? b.createdAt) < BULK_RECENT_MS);

/**
 * The runs to show in the tray: running ones, and those finished in the last ten minutes. The recent list (the
 * person's own; everyone's for owners and admins) is read every 5 seconds while something runs, else every minute;
 * `bulk` events keep it current in between.
 */
export function useBulkRuns(): BulkRun[] {
  const org = useOrg();
  const now = useNow(true, 15_000);
  const recent = useQuery({
    queryKey: recentKey(org.id),
    queryFn: async ({ signal }) => ((await api.get<{ runs: Raw[] }>(`/orgs/${org.id}/bulk?recent=1`, signal)).runs ?? []).map(runFrom).filter((b): b is BulkRun => !!b),
    staleTime: 3_000,
    retry: 1,
    refetchInterval: (q) => ((q.state.data ?? []).some((b) => !runFinished(b)) ? 5_000 : 60_000),
  });
  const known = new Set((recent.data ?? []).map((b) => b.id));
  const extra = useStartedHere(org.id).filter((id) => !known.has(id));
  const own = useQueries({
    queries: extra.map((id) => ({
      queryKey: bulkKey(org.id, id),
      queryFn: async ({ signal }: { signal: AbortSignal }) => runFrom((await api.get<{ bulk: Raw }>(`/orgs/${org.id}/bulk/${id}`, signal)).bulk),
      staleTime: 2_000,
      retry: 1,
      refetchInterval: (q: { state: { data: BulkRun | null | undefined } }) => (q.state.data && !runFinished(q.state.data) ? 3_000 : false),
    })),
  });
  return [...own.map((q) => q.data).filter((b): b is BulkRun => !!b), ...(recent.data ?? [])].filter((b) => inTray(b, now));
}

/**
 * The operations a run's work is made of, so the tray doesn't list them again on their own: each item's, and the
 * other half of its chain. An item follows its restore point's operation and then, once that hands on, the update's,
 * so both directions count: the work after a restore point, and the restore point before the work.
 */
export function runOperationIds(runs: BulkRun[], ops: Operation[]): Set<string> {
  const ids = new Set(runs.flatMap((b) => b.items.map((i) => i.operationId).filter((x): x is string => !!x)));
  for (const o of ops) {
    const next = o.followUp?.operationId;
    if (!next) continue;
    if (ids.has(o.id)) ids.add(next);
    else if (ids.has(next)) ids.add(o.id);
  }
  return ids;
}

/* ── Planning and starting ──────────────────────────────────────── */

/** The dry run: what each site would get. Asked again whenever the sites, the action or its params change. */
export function useBulkPlan(siteIds: string[], action: BulkAction | null, params: BulkParams, enabled = true) {
  const org = useOrg();
  const ids = [...siteIds].sort();
  return useQuery({
    queryKey: ["org", org.id, "bulk-plan", action, params, ids],
    queryFn: async () => planFrom(await api.post<Raw>(`/orgs/${org.id}/sites/bulk`, { siteIds: ids, action, params, dryRun: true }), action!),
    enabled: enabled && !!action && ids.length > 0,
    staleTime: 0,
    gcTime: 30_000,
    retry: false,
  });
}

export type Started = { bulkId: string | null; action: BulkAction; items: BulkItem[] };

/** Starts a run; it's followed in the tray, and the sites refresh as their work finishes. */
export function useStartBulk() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { siteIds: string[]; action: BulkAction; params: BulkParams }): Promise<Started> => {
      const r = await api.post<Raw>(`/orgs/${org.id}/sites/bulk`, { ...v, dryRun: false });
      return { bulkId: str(r.bulkId), action: v.action, items: ((r.results ?? []) as Raw[]).map((x) => itemFrom(x, false)) };
    },
    onSuccess: (r) => {
      // Queued work goes to the tray; at-once changes (tags, automatic updates) are done in the answer.
      if (r.bulkId && r.items.some(isActiveItem)) noteStarted(org.id, r.bulkId);
      void qc.invalidateQueries({ queryKey: recentKey(org.id) });
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "org" && (q.queryKey[2] === "sites" || q.queryKey[2] === "site-tags") });
      void qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}

/** A `bulk` event (a `BulkProgress`): the run's new state in the tray's lists. Read the sites again when it ends. */
export function applyBulkEvent(qc: QueryClient, orgId: string, data: Raw) {
  const run = runFrom(data);
  if (!run) return;
  // The event comes on the person's own stream whichever org is open: a run asked from another org is in their work
  // elsewhere (api/work.ts), which the tray adds to this org's.
  if (run.orgId && run.orgId !== orgId) {
    const work = qc.getQueryData<Work>(keys.myWork);
    const had = work?.runs.find((b) => b.id === run.id);
    if (work && had) qc.setQueryData<Work>(keys.myWork, { ...work, runs: work.runs.map((b) => (b.id === run.id ? { ...run, org: had.org } : b)) });
    else void qc.invalidateQueries({ queryKey: keys.myWork });
    return;
  }
  qc.setQueryData(bulkKey(orgId, run.id), run);
  const list = qc.getQueryData<BulkRun[]>(recentKey(orgId));
  if (list) qc.setQueryData(recentKey(orgId), list.some((b) => b.id === run.id) ? list.map((b) => (b.id === run.id ? run : b)) : [run, ...list]);
  else void qc.invalidateQueries({ queryKey: recentKey(orgId) });
  // The Updates page's list too: what finished is no longer waiting.
  if (runFinished(run)) void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "org" && (q.queryKey[2] === "sites" || q.queryKey[2] === "updates") });
}
